/**
 * Scripture lookup via POST /chat/scripture-lookup.
 * Port of flock-expo/src/hooks/useScriptureLookup.ts (AsyncStorage → localStorage).
 */
import { useCallback, useState } from 'react';
import { api } from '@/lib/apiClient';
import type { BibleTranslation, ScriptureReference } from '@flock-types/chat.types';

const STORAGE_KEY = 'scripture_preferred_translation';
const DEFAULT_TRANSLATION: BibleTranslation = 'NIV';

interface ScriptureLookupResponse {
  success: boolean;
  data: {
    reference: string;
    text: string;
    translation: string;
    truncated: boolean;
    bookAbbr: string;
    chapter: number;
    startVerse: number;
  };
  message?: string;
}

export function useScriptureLookup() {
  const [translation, setTranslationState] = useState<BibleTranslation>(
    () => (localStorage.getItem(STORAGE_KEY) as BibleTranslation) || DEFAULT_TRANSLATION
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScriptureReference | null>(null);

  const setTranslation = useCallback((t: BibleTranslation) => {
    setTranslationState(t);
    localStorage.setItem(STORAGE_KEY, t);
  }, []);

  const lookup = useCallback(
    async (reference: string): Promise<ScriptureReference | null> => {
      if (!reference.trim()) {
        setError('Please enter a scripture reference');
        return null;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await api.post<ScriptureLookupResponse>('/chat/scripture-lookup', {
          reference: reference.trim(),
          translation,
        });
        if (response.success) {
          const data = response.data;
          const scripture: ScriptureReference = {
            reference: data.reference,
            text: data.text,
            translation: data.translation as BibleTranslation,
            bookAbbr: data.bookAbbr,
            chapter: data.chapter,
            startVerse: data.startVerse,
          };
          setResult(scripture);
          return scripture;
        }
        setError(response.message || 'Could not find that verse');
        return null;
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || 'Failed to look up verse');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [translation]
  );

  return {
    lookup,
    loading,
    error,
    result,
    translation,
    setTranslation,
    clearError: useCallback(() => setError(null), []),
    clearResult: useCallback(() => {
      setResult(null);
      setError(null);
    }, []),
  };
}

export default useScriptureLookup;
